import { useEffect, useMemo, useRef, useState } from "react";
import { Archive, CalendarDays, Camera, ExternalLink, Eye, FileText, Folder, ImageIcon, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  PATIENT_FILE_CATEGORIES,
  archivePatientFile,
  createSignedPatientFileUrl,
  createSignedPatientFileUrls,
  listPatientFiles,
  uploadPatientFile,
} from "@/lib/patientFilesApi";

const STAGES = [
  { id: "initial", label: "Initial" },
  { id: "progress", label: "Progress" },
  { id: "final", label: "Final" },
];

export default function PatientFilesTab({ patientId, patient, demoFiles = null }) {
  const fileInputRef = useRef(null);
  const [payload, setPayload] = useState(() => ({
    files: demoFiles || [],
    ortho_cases: [],
    clinic_id: patient?.clinic_id || null,
  }));
  const [signedUrls, setSignedUrls] = useState({});
  const [loading, setLoading] = useState(() => !demoFiles);
  const [saving, setSaving] = useState(false);
  const [previewingId, setPreviewingId] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedFile, setSelectedFile] = useState(null);
  const [form, setForm] = useState(() => emptyForm());

  const hasOrthoCase = (payload.ortho_cases || []).length > 0;
  const visibleCategories = useMemo(
    () => PATIENT_FILE_CATEGORIES.filter((category) => category.id !== "ortho_case_photos" || hasOrthoCase),
    [hasOrthoCase]
  );
  const activeFiles = useMemo(
    () => sortFilesByCaptureDate((payload.files || []).map(normalizeFileRow).filter((file) => file.status !== "archived")),
    [payload.files]
  );
  const filteredFiles = selectedCategory === "all"
    ? activeFiles
    : activeFiles.filter((file) => file.category === selectedCategory);
  const latestCaptureDate = activeFiles[0]?.capture_date || null;

  useEffect(() => {
    if (demoFiles) return undefined;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const nextPayload = await listPatientFiles(patientId);
        if (cancelled) return;
        setPayload(nextPayload);
        const imagePaths = (nextPayload.files || [])
          .filter((file) => file.file_kind === "image" && file.status !== "archived")
          .map((file) => file.storage_path);
        const urls = await createSignedPatientFileUrls(imagePaths);
        if (!cancelled) setSignedUrls(urls || {});
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not load patient files.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [patientId, demoFiles]);

  useEffect(() => {
    if (form.category !== "ortho_case_photos" || hasOrthoCase) return;
    setForm((current) => ({ ...current, category: "xray_imaging", orthoCaseId: "", orthoStage: "" }));
  }, [form.category, hasOrthoCase]);

  async function reloadFiles() {
    const nextPayload = await listPatientFiles(patientId);
    setPayload(nextPayload);
    const imagePaths = (nextPayload.files || [])
      .filter((file) => file.file_kind === "image" && file.status !== "archived")
      .map((file) => file.storage_path);
    setSignedUrls((await createSignedPatientFileUrls(imagePaths)) || {});
  }

  async function submitUpload(event) {
    event.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error("Pick a file first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await uploadPatientFile({
        patientId,
        clinicId: payload.clinic_id || patient?.clinic_id,
        file,
        category: form.category,
        captureDate: form.captureDate,
        visitId: form.visitId,
        treatmentPlanId: form.treatmentPlanId,
        orthoCaseId: form.orthoCaseId,
        orthoStage: form.orthoStage,
      });
      toast.success("File uploaded.");
      setForm(emptyForm());
      if (fileInputRef.current) fileInputRef.current.value = "";
      await reloadFiles();
    } catch (err) {
      setError(err.message || "Could not upload file.");
      toast.error(err.message || "Could not upload file.");
    } finally {
      setSaving(false);
    }
  }

  async function archiveFile(file) {
    setSaving(true);
    setError("");
    try {
      await archivePatientFile(file.id);
      toast.success("File archived.");
      setSelectedFile((current) => current?.id === file.id ? null : current);
      await reloadFiles();
    } catch (err) {
      setError(err.message || "Could not archive file.");
      toast.error(err.message || "Could not archive file.");
    } finally {
      setSaving(false);
    }
  }

  async function previewFileFor(file) {
    setPreviewingId(file.id);
    setError("");
    try {
      const signedUrl = await createSignedPatientFileUrl(file.storage_path);
      setPreviewFile({ file, signedUrl });
    } catch (err) {
      setError(err.message || "Could not preview this file.");
      toast.error(err.message || "Could not preview this file.");
    } finally {
      setPreviewingId("");
    }
  }

  if (loading) return <div className="text-sm text-slate-400 px-2">Loading files...</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="patient-files-tab">
      <div className="px-5 py-4 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Files</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span data-testid="patient-files-count">{activeFiles.length} {activeFiles.length === 1 ? "file" : "files"}</span>
            <span aria-hidden="true">/</span>
            <span data-testid="patient-files-latest">Latest capture {formatDate(latestCaptureDate)}</span>
          </div>
        </div>
        <form onSubmit={submitUpload} className="grid grid-cols-1 md:grid-cols-[minmax(140px,1fr)_140px_minmax(160px,1fr)_auto] gap-2 lg:max-w-4xl">
          <select
            value={form.category}
            onChange={(event) => setFormValue("category", event.target.value)}
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
            aria-label="File category"
            disabled={saving}
          >
            {visibleCategories.map((category) => (
              <option key={category.id} value={category.id}>{category.shortLabel || category.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={form.captureDate}
            onChange={(event) => setFormValue("captureDate", event.target.value)}
            className="h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-700"
            aria-label="Capture date"
            disabled={saving}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="h-10 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 file:mr-3 file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-slate-700"
            aria-label="Patient file"
            disabled={saving}
          />
          <button
            type="submit"
            disabled={saving}
            className="h-10 inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            <Upload className="w-4 h-4" />
            Upload
          </button>
          {form.category === "ortho_case_photos" ? (
            <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select
                value={form.orthoCaseId}
                onChange={(event) => setFormValue("orthoCaseId", event.target.value)}
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
                aria-label="Ortho case"
                disabled={saving}
              >
                <option value="">Select ortho case</option>
                {(payload.ortho_cases || []).map((orthoCase) => (
                  <option key={orthoCase.id} value={orthoCase.id}>{orthoCase.label || "Ortho case"}</option>
                ))}
              </select>
              <select
                value={form.orthoStage}
                onChange={(event) => setFormValue("orthoStage", event.target.value)}
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
                aria-label="Ortho stage"
                disabled={saving}
              >
                <option value="">Select stage</option>
                {STAGES.map((stage) => <option key={stage.id} value={stage.id}>{stage.label}</option>)}
              </select>
            </div>
          ) : null}
          <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={form.visitId}
              onChange={(event) => setFormValue("visitId", event.target.value)}
              className="h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Visit ID"
              aria-label="Optional visit ID"
              disabled={saving}
            />
            <input
              value={form.treatmentPlanId}
              onChange={(event) => setFormValue("treatmentPlanId", event.target.value)}
              className="h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Treatment plan ID"
              aria-label="Optional treatment plan ID"
              disabled={saving}
            />
          </div>
        </form>
      </div>

      {error ? <div className="mx-5 mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 overflow-x-auto">
        <FilterButton active={selectedCategory === "all"} onClick={() => setSelectedCategory("all")}>All</FilterButton>
        {visibleCategories.map((category) => (
          <FilterButton key={category.id} active={selectedCategory === category.id} onClick={() => setSelectedCategory(category.id)}>
            {category.shortLabel || category.label}
          </FilterButton>
        ))}
      </div>

      <div className="px-5 py-5 space-y-7">
        {filteredFiles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center">
            <Folder className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-slate-800">No active files.</p>
            <p className="mt-1 text-xs text-slate-500">Archived files stay retained outside this list.</p>
          </div>
        ) : visibleCategories.map((category) => {
          const files = filteredFiles.filter((file) => file.category === category.id);
          if (files.length === 0) return null;
          return (
            <section key={category.id} data-testid={`patient-file-category-${category.id}`} className="space-y-3">
              <div className="flex items-center gap-2">
                <Folder className="w-4 h-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-900">{category.label}</h3>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{files.length}</span>
              </div>
              {category.id === "ortho_case_photos" ? (
                <OrthoStageGroups files={files} signedUrls={signedUrls} selectedFile={selectedFile} onSelect={setSelectedFile} onArchive={archiveFile} onPreview={previewFileFor} saving={saving} previewingId={previewingId} />
              ) : category.view === "list" ? (
                <DocumentList files={files} selectedFile={selectedFile} onSelect={setSelectedFile} onArchive={archiveFile} onPreview={previewFileFor} saving={saving} previewingId={previewingId} />
              ) : (
                <FileGrid files={files} signedUrls={signedUrls} selectedFile={selectedFile} onSelect={setSelectedFile} onArchive={archiveFile} onPreview={previewFileFor} saving={saving} previewingId={previewingId} />
              )}
            </section>
          );
        })}
      </div>

      {selectedFile ? <FileDetail file={selectedFile} signedUrl={signedUrls[selectedFile.storage_path]} /> : null}
      {previewFile ? (
        <FilePreviewOverlay
          file={previewFile.file}
          signedUrl={previewFile.signedUrl}
          onClose={() => setPreviewFile(null)}
        />
      ) : null}
    </div>
  );

  function setFormValue(key, value) {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "category" && value !== "ortho_case_photos") {
        next.orthoCaseId = "";
        next.orthoStage = "";
      }
      return next;
    });
  }
}

function FilterButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 shrink-0 rounded-full border px-3 text-xs font-semibold transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
    >
      {children}
    </button>
  );
}

function OrthoStageGroups({ files, signedUrls = {}, selectedFile, onSelect, onArchive, onPreview, saving, previewingId }) {
  return (
    <div className="space-y-5">
      {STAGES.map((stage) => {
        const stageFiles = files.filter((file) => file.ortho_stage === stage.id);
        if (stageFiles.length === 0) return null;
        return (
          <div key={stage.id} data-testid={`patient-file-stage-${stage.id}`} className="space-y-2">
            <p className="text-xs font-semibold text-slate-500">{stage.label}</p>
            <FileGrid files={stageFiles} signedUrls={signedUrls} selectedFile={selectedFile} onSelect={onSelect} onArchive={onArchive} onPreview={onPreview} saving={saving} previewingId={previewingId} />
          </div>
        );
      })}
    </div>
  );
}

function FileGrid({ files, signedUrls = {}, selectedFile, onSelect, onArchive, onPreview, saving, previewingId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {files.map((file) => (
        <FileTile
          key={file.id}
          file={file}
          signedUrl={signedUrls[file.storage_path]}
          selected={selectedFile?.id === file.id}
          onSelect={onSelect}
          onArchive={onArchive}
          onPreview={onPreview}
          saving={saving}
          previewing={previewingId === file.id}
        />
      ))}
    </div>
  );
}

function FileTile({ file, signedUrl, selected, onSelect, onArchive, onPreview, saving, previewing }) {
  return (
    <article className={`rounded-xl border p-2 ${selected ? "border-slate-900" : "border-slate-200"}`}>
      <button type="button" onClick={() => onSelect(file)} className="block w-full text-left">
        <div className="aspect-[4/3] rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
          {signedUrl ? (
            <img src={signedUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="w-8 h-8 text-slate-300" />
          )}
        </div>
        <FileTextBlock file={file} />
      </button>
      <div className="mt-2 flex items-center justify-between gap-2">
        <PreviewButton file={file} onPreview={onPreview} saving={saving} previewing={previewing} />
        <ArchiveButton file={file} onArchive={onArchive} saving={saving} />
      </div>
    </article>
  );
}

function DocumentList({ files, selectedFile, onSelect, onArchive, onPreview, saving, previewingId }) {
  return (
    <div className="space-y-2" data-testid="patient-file-documents-list">
      {files.map((file) => (
        <div key={file.id} className={`flex items-center gap-3 rounded-xl border px-3 py-3 ${selectedFile?.id === file.id ? "border-slate-900" : "border-slate-200"}`}>
          <button type="button" onClick={() => onSelect(file)} className="min-w-0 flex flex-1 items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-slate-500" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900" data-testid={`patient-file-name-${file.id}`}>{file.original_filename}</p>
              <p className="text-xs text-slate-500">{formatDate(file.capture_date)} / {file.uploaded_by_name || "Unknown user"}</p>
            </div>
          </button>
          <div className="flex shrink-0 items-center gap-1">
            <PreviewButton file={file} onPreview={onPreview} saving={saving} previewing={previewingId === file.id} />
            <ArchiveButton file={file} onArchive={onArchive} saving={saving} />
          </div>
        </div>
      ))}
    </div>
  );
}

function FileTextBlock({ file }) {
  return (
    <div className="mt-2 min-w-0">
      <p className="truncate text-sm font-semibold text-slate-900" data-testid={`patient-file-name-${file.id}`}>{file.original_filename}</p>
      <p className="mt-0.5 text-xs text-slate-500">{formatDate(file.capture_date)} / {file.uploaded_by_name || "Unknown user"}</p>
    </div>
  );
}

function ArchiveButton({ file, onArchive, saving }) {
  return (
    <button
      type="button"
      onClick={() => onArchive(file)}
      disabled={saving}
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60"
      aria-label={`Archive ${file.original_filename}`}
      title="Archive"
    >
      <Archive className="w-4 h-4" />
    </button>
  );
}

function PreviewButton({ file, onPreview, saving, previewing }) {
  return (
    <button
      type="button"
      onClick={() => onPreview(file)}
      disabled={saving || previewing}
      className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-60"
      aria-label={`Preview ${file.original_filename}`}
      title="Preview"
    >
      {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
      Preview
    </button>
  );
}

function FilePreviewOverlay({ file, signedUrl, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 px-4 py-6" role="dialog" aria-modal="true" aria-label={`Preview ${file.original_filename}`}>
      <div className="mx-auto flex h-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">{file.original_filename}</p>
            <p className="text-xs text-slate-500">Capture {formatDate(file.capture_date)} / {file.uploaded_by_name || "Unknown user"}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <a
              href={signedUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label={`Open ${file.original_filename} in a new tab`}
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close preview"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 bg-slate-100">
          {file.file_kind === "image" ? (
            <div className="flex h-full items-center justify-center p-4">
              <img src={signedUrl} alt={file.original_filename} className="max-h-full max-w-full rounded-lg object-contain shadow-sm" />
            </div>
          ) : (
            <iframe title={file.original_filename} src={signedUrl} className="h-full w-full border-0 bg-white" />
          )}
        </div>
      </div>
    </div>
  );
}

function FileDetail({ file, signedUrl }) {
  return (
    <aside className="border-t border-slate-100 bg-slate-50 px-5 py-4">
      <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-4">
        <div className="aspect-[4/3] rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
          {file.file_kind === "image" && signedUrl ? (
            <img src={signedUrl} alt="" className="h-full w-full object-cover" />
          ) : file.file_kind === "image" ? (
            <Camera className="w-8 h-8 text-slate-300" />
          ) : (
            <FileText className="w-8 h-8 text-slate-300" />
          )}
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 break-words">{file.original_filename}</h3>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <DetailItem icon={CalendarDays} label="Capture date" value={formatDate(file.capture_date)} />
            <DetailItem icon={Upload} label="Uploaded" value={`${formatDateTime(file.uploaded_at)} by ${file.uploaded_by_name || "Unknown user"}`} />
            <DetailItem icon={Folder} label="Category" value={categoryLabel(file.category)} />
            <DetailItem icon={FileText} label="Storage path" value={file.storage_path} />
          </dl>
        </div>
      </div>
    </aside>
  );
}

function DetailItem({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <dt className="flex items-center gap-1.5 font-semibold text-slate-500"><Icon className="w-3.5 h-3.5" /> {label}</dt>
      <dd className="mt-1 break-words text-slate-800">{value || "None"}</dd>
    </div>
  );
}

function sortFilesByCaptureDate(files) {
  return [...files].sort((a, b) => {
    const captureDiff = new Date(b.capture_date || 0).getTime() - new Date(a.capture_date || 0).getTime();
    if (captureDiff !== 0) return captureDiff;
    return new Date(b.uploaded_at || 0).getTime() - new Date(a.uploaded_at || 0).getTime();
  });
}

function normalizeFileRow(file) {
  return {
    status: "active",
    category: categoryForLegacyType(file.type) || "documents",
    original_filename: file.original_filename || file.name || "Untitled file",
    capture_date: file.capture_date || file.date || null,
    uploaded_by_name: file.uploaded_by_name || file.owner || "Unknown user",
    file_kind: file.file_kind || (file.mime_type === "application/pdf" ? "document" : "image"),
    storage_path: file.storage_path || "",
    ...file,
  };
}

function categoryForLegacyType(type) {
  const value = String(type || "").toLowerCase();
  if (value.includes("x-ray") || value.includes("xray")) return "xray_imaging";
  if (value.includes("photo")) return "clinical_photos";
  if (value.includes("document") || value.includes("consent")) return "documents";
  return null;
}

function emptyForm() {
  return {
    category: "xray_imaging",
    captureDate: new Date().toISOString().slice(0, 10),
    visitId: "",
    treatmentPlanId: "",
    orthoCaseId: "",
    orthoStage: "",
  };
}

function categoryLabel(categoryId) {
  return PATIENT_FILE_CATEGORIES.find((category) => category.id === categoryId)?.label || categoryId;
}

function formatDate(iso) {
  if (!iso) return "No capture date";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(iso) {
  if (!iso) return "No upload time";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
